import { DeltakerTypes, IDeltaker, IDeltakerMutateVars } from '../types'
import {
  useAddDeltaker,
  useRemoveDeltaker,
  useUpdateDeltakerNavn,
} from './query-hooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useIsMutating,
  UseMutateFunction,
  UseQueryResult,
} from '@tanstack/react-query'
import { initialDeltaker } from '../queries/helpers'
import { AxiosResponse } from 'axios'

type IUseDeltakereFns = (
  query: UseQueryResult<IDeltaker[]>,
  kvitteringId: string,
  type: DeltakerTypes,
  noInitialData?: boolean
) => {
  onAdd: () => void
  onUpdate: (deltaker: IDeltaker) => void
  onRemove: (deltaker: IDeltaker) => void
  remove: () => void
  hasError: boolean
  shouldAutoFocus: (deltaker: IDeltaker) => boolean
  deltakerToBeRemoved: IDeltaker | undefined
  setDeltakerToBeRemoved: (deltaker: IDeltaker | undefined) => void
  isAddLoading: boolean
}

export const useDeltakereFns: IUseDeltakereFns = (
  query,
  kvitteringId,
  type,
  noInitialData = false
) => {
  const deltakere = useMemo(() => query.data ?? [], [query.data])

  const [hasError, setHasError] = useState(false)
  const [deltakerToBeRemoved, setDeltakerToBeRemoved] = useState<
    IDeltaker | undefined
  >(undefined)
  const [shouldFocus, setShouldFocus] = useState(false)

  const {
    mutate: addDeltaker,
    isPending: isAddPending,
    isPaused: isAddPaused,
  } = useAddDeltaker(type, kvitteringId)

  const { mutate: updateDeltaker } = useUpdateDeltakerNavn(type, kvitteringId)

  const { mutate: removeDeltaker } = useRemoveDeltaker(type, kvitteringId)

  const onAdd = useCallback(() => {
    const isLastDeltakerEmpty =
      deltakere[deltakere.length - 1]?.navn.length === 0
    if (isLastDeltakerEmpty) {
      setHasError(true)
      return
    }

    setHasError(false)
    setShouldFocus(true)
    addDeltaker({ deltaker: initialDeltaker(), kvitteringId, type })
  }, [deltakere, kvitteringId, type, addDeltaker])

  const onUpdate = useCallback(
    (selectedDeltaker: IDeltaker) => {
      updateDeltaker({ deltaker: selectedDeltaker, kvitteringId, type })
      setShouldFocus(true)
    },
    [kvitteringId, type, updateDeltaker]
  )

  const remove = useCallback(() => {
    if (!deltakerToBeRemoved) return

    removeDeltaker({ deltaker: deltakerToBeRemoved, kvitteringId, type })
    setDeltakerToBeRemoved(undefined)
  }, [deltakerToBeRemoved, kvitteringId, type, removeDeltaker])

  const onRemove = useCallback(
    (deltaker: IDeltaker) => {
      if (deltaker.navn.length > 0) {
        setDeltakerToBeRemoved(deltaker)
        return
      }

      removeDeltaker({ deltaker, kvitteringId, type })
    },
    [kvitteringId, type, removeDeltaker]
  )

  const shouldAutoFocus = useCallback(
    (deltaker: IDeltaker) =>
      shouldFocus && deltaker.id === deltakere[deltakere.length - 1].id,
    [deltakere, shouldFocus]
  )

  useAddEmptyDeltaker(
    deltakere,
    kvitteringId,
    type,
    !noInitialData && !query.isFetching,
    addDeltaker
  )

  return {
    onAdd,
    onUpdate,
    onRemove,
    remove,
    shouldAutoFocus,
    setDeltakerToBeRemoved,
    hasError,
    deltakerToBeRemoved,
    isAddLoading: isAddPending && !isAddPaused,
  }
}

export const useRemoveEmptyDeltaker = (
  deltakere: IDeltaker[],
  removeDeltaker: (deltaker: IDeltaker) => void
) => {
  const deltakereRef = useRef(deltakere)
  const removeRef = useRef(removeDeltaker)

  useEffect(() => {
    deltakereRef.current = deltakere
    removeRef.current = removeDeltaker
  }, [deltakere, removeDeltaker])

  useEffect(() => {
    return () => {
      const deltakere = deltakereRef.current ?? []
      const remove = removeRef.current

      const lastDeltaker = deltakere[deltakere.length - 1]
      if (remove && lastDeltaker?.navn.length === 0) {
        remove(lastDeltaker)
      }
    }
  }, [])
}

const useAddEmptyDeltaker = (
  deltakere: IDeltaker[],
  kvitteringId: string,
  type: DeltakerTypes,
  shouldAdd: boolean,
  addDeltaker: UseMutateFunction<
    AxiosResponse<null, any>,
    string | Error,
    IDeltakerMutateVars,
    {
      previousDeltakere: IDeltaker[]
    }
  >
) => {
  const isPendingAddDeltaker = useIsMutating({
    predicate: (mutation) =>
      (mutation.options.mutationKey?.[0] as any).entity.includes('deltaker') &&
      (mutation.options.mutationKey?.[0] as any).action === 'add' &&
      (mutation.state.status === 'pending' || mutation.state.isPaused),
  })

  useEffect(() => {
    if (shouldAdd && isPendingAddDeltaker === 0 && deltakere.length === 0) {
      addDeltaker({ kvitteringId, deltaker: initialDeltaker(), type })
    }
  }, [
    isPendingAddDeltaker,
    deltakere,
    kvitteringId,
    shouldAdd,
    type,
    addDeltaker,
  ])
}
